import type { InvoiceSubscription, RecurlyCardDetails } from '~/types/checkout'
import type { ApiErrorResponse } from '~/utils/http/api-error'
import type { Pagination } from '~/types/utility'
import type {Culture, Language} from '~/types/locale';
import type { AddressPayload } from '~/types/address'
import type { DemoPaymentInformation } from '~/types/demo'

export enum UserRole {
  Anonymous = 'Anonymous',
  Guest = 'Guest',
  Customer = 'Customer',
  Recruit = 'Recruit',
  Demonstrator = 'Demonstrator',
  Agent = 'Agent'
}

export interface ContactInfo {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  birthday: string
  preferredContact: string
  preferredLanguage?: string
}

export interface Demonstrator {
  demoId: number
  isArchived: boolean
  email: string
  status?: unknown
  statusName: string
  region: string
  city: string
  suburb?: string
  lastName: string
  firstName: string
  fullName: string
  culture?: unknown
  county?: string
  country: string
  canOrderFrom: boolean
  defaultDemonstrator: boolean
  // Attribution-time UTC stamp from CartsV2; present on the JSON, used by
  // pre-attribution telemetry. DateTime.MinValue ("0001-01-01...") = unset.
  timestamp?: string
  socialMedia?: {
    facebook?: string
    instagram?: string
    twitter?: string
    pinterest?: string
    youtube?: string
    web: string
  }
  phoneNumber: string
  titleName: string
  title: number
  hasDbws: boolean
  uplineId?: number
  directToStampinUp?: boolean
  dbwsStatus?: string
}

export interface DemonstratorPhoto {
  demoId: number
  photo?: string
}

export interface DemonstratorPhotoCache {
  photos: DemonstratorPhoto[]
}

export interface NameFields {
  firstName: string
  lastName: string
}

export interface DemoCustomerCreate extends NameFields {
  email: string
}

export interface Address extends NameFields, AddressPayload {
  id: string
  city: string
  postalCode: string
  country: string
  phoneNumber: string
  email?: string
  defaultAddress: boolean
  defaultMailingAddress?: boolean
  timestamp?: string
}

export enum BankCardType {
  AmericanExpress = 'American Express',
  Mastercard = 'Mastercard',
  Visa = 'Visa',
  Discover = 'Discover',
  JCB = 'JCB',
  Paypal = 'Paypal',
  Ideal = 'Ideal',
  Bancontact = 'Bancontact'
}

interface PaymentInformationBase {
  expirationMonth: number | null
  expirationYear: number | null
  cardName: string
  isDefaultPayment: boolean
  saveToProfile?: boolean
}

interface RecurlyPaymentBase {
  cardName: string
  isDefaultPayment: boolean
}

export interface PaymentInformation extends PaymentInformationBase {
  paymentAuthorizationId: string
  billingInfoId: string
  paymentAccountId?: number
  recurlyToken?: string
  paymentType: string
  accountLast4: string
  friendlyCardType: BankCardType
  isExpired: boolean
  isOwnedByDemo?: boolean
  status: string
}
export interface RecurlyPaymentInformation extends PaymentInformation, Address {
}
export interface RecurlyPaymentUpdate extends Omit<RecurlyPaymentBase, 'cardName'>, Omit<Address, 'id' | 'timestamp' | 'defaultAddress'> {
  billingInfoId?: string
  saveToProfile?: boolean
  recurlyToken?: string
  isOwnedByDemo?: boolean
  cardDetails?: RecurlyCardDetails
  // Set when the recurlyToken came from Recurly's PayPal Complete flow so the
  // backend stores it as a PayPal billing agreement (paymentAuthorizationId=paypal).
  isPaypal?: boolean
}
export interface RecurlyPaymentCreate {
  recurlyToken: string
  isDefaultPayment: boolean
}

export interface RecurlyPaymentCreateResponse extends RecurlyPaymentInformation {
  recurlyAccountCode: string
  recurlyActionTokenId?: string
  recurlyToken: string
  recurlyWants3DSecure?: boolean
}

export type IncompleteAddress = Omit<Address, 'id' | 'timestamp' | 'firstName' | 'lastName' | 'defaultAddress'>
export type IncompletePayment = Omit<PaymentInformation, 'friendlyCardType'>

export interface PaymentRecurlyUpdate {
  address: IncompleteAddress
  names: NameFields
  isDefaultPayment: boolean
  isOwnedByDemo?: boolean
  saveToProfile?: boolean
}

export interface AnonymousLoginResponse {
  bearerToken: string
  cartId?: string
}

export interface OrderPayment {
  paymentAmount: string
  paymentType: string
  accountLast4Digits: string
  creditCardExpiration: string
  referenceNumber: string
}

export interface AccountOrderDetail {
  lineNumber: number
  itemId: string
  slug: string
  itemType: string
  qtyOrdered: number
  description: string
  formattedItemPrice: string
  customerName?: string
  formattedLineTotal: string
  guestId?: string
}

export interface OrderCouponCode {
  couponCode: string
  couponValue: number
}

export interface CountryListItem {
  name: string
  code: string
  suRegion: string
  stateType: string
  cultures: string[]
  states: string[]
}

export interface ActivePoints {
  currentBalance: number
  formattedCurrentBalance: string
}

export interface ExpiringGroup {
  priority: number
  formattedAmount: string
  date: string
  amount: number
}

export interface ExpiringOverview {
  expiringGroups: ExpiringGroup[]
  formattedTotalExpiring: string
  totalExpiring: number
}

export interface LoyaltyTransaction {
  created: string
  description?: string
  amount?: number
  formattedAmount?: string
  type: string
  orderId?: string
  orderConfirmationNumber?: string
  name?: string
  userName?: string
  isProxy?: boolean
  isProxyGift?: boolean
  role?: string
}

export interface LoyaltyDashboard {
  balance?: ActivePoints
  expiring: ExpiringOverview
  transactions: LoyaltyTransaction[]
  totalPageCount: number
}

export enum SubscriptionStatus{
  OptedIn = 'OptedIn',
  OptedOut = 'OptedOut',
  Deleted = 'Deleted'
}

export interface DemonstratorSubscription {
  demoId: number
  demonstrator: Demonstrator
  subscriptionStatus: SubscriptionStatus
  lastModifiedDate: Date
  defaultDemonstrator: boolean
}

export interface TrackingNumber {
  tracking?: string
  shipDate: string
  formattedShipDate: string
  trackingUrl: string
  shippingMethod: string
  orderDetails: AccountOrderDetail[]
}

export interface Order {
  confirmationEmail?: string
  status: string
  orderDate: string
  formattedOrderDate: string
  customerName?: string
  orderType?: string
  formattedCsvAmount?: string
  orderTotal: number
  formattedOrderTotal: string
  taxTotal: number
  formattedTaxTotal: string
  countryTaxAmount?: string
  regionTaxAmount?: string
  invoiceId: string
  orderConfirmationNumber: string
  recurlySubscriptionId?: string
  orderId: number
  orderSubtotal: number
  formattedOrderSubTotal: string
  loyaltyPointsEarned?: number
  loyaltyPointsRedeemed?: number
  formattedLoyaltyPointsEarned?: string
  formattedLoyaltyPointsRedeemed?: string
  formattedTotalCouponAmount: string
  totalCouponAmount: number
  totalQtyOrdered: number
  shippingTotal: number
  formattedShippingTotal: string
  formattedRetailDeliveryFee?: string
  shippingAddress: Address
  trackingNumbers: TrackingNumber[]
  shipmentDate: string
  shippingMethodName: string
  formattedShipmentDate: string
  shippingMethod: number
  orderDetails: AccountOrderDetail[]
  orderPayments: OrderPayment[]
  demonstrator: Demonstrator
  groupShippingCode: string
  orderCouponCode: OrderCouponCode
  hasMailingCustomers?: boolean
  isOnlineOrder?: boolean
  isProxyOrder?: boolean
  demoGetsProxyOrderRewards?: boolean
  formattedCustomerTotal?: string
  isFirstOrder?: boolean
  // Set by the API for Paper Pumpkin subscription orders; drives the
  // "Paper Pumpkin" tag under the order number in order history.
  isSubscriptionOrder?: boolean
}

// todo adjust field names to real after backend
export interface DemoOrder extends Order {
  formattedCommissionableSubTotal: string
  formattedNonCommissionableSubTotal: string
  formattedInstantIncomePercent: string
  formattedInstantIncomeAmount: string
  formattedCustomerPaymentTotal: string
  formattedDemoPaymentTotal: string
  formattedPayToDemo: string
  commissionableSubTotal: number
  nonCommissionableSubTotal: number
  totalCreditCommissionable: number
  totalCreditNonCommissionable: number
  instantIncomeAmount: number
  instantIncomePercent: number
}

export interface DemoSubscription extends Order {
  subscriptionSubTotal: number,
  formattedSubscriptionSubTotal: string,
  subscriptionTotal: number,
  formattedSubscriptionTotal: string,
}

export interface DemoOrderListItem {
  customerName: string
  customerId?: string
  confirmationEmail: string
  orderDate: string
  orderId: number
  orderType: string
  formattedOrderDate: string
  orderTotal: number
  formattedOrderTotal: string
  csv: number
  formattedCsvAmount: string
  status: string
  totalQtyOrdered: number
  // Set by the API when this is the customer's first order; drives the
  // first-order indicator on the customer order history (feature-gated).
  isFirstOrder?: boolean
  // Set by the API for Paper Pumpkin subscription orders; drives the
  // "Paper Pumpkin" tag under the order number in order history.
  isSubscriptionOrder?: boolean
}

export interface OrderHistoryResponse extends Pagination {
  errorMessage?: string
  orders: Order[]
}
export interface OrderDemoHistoryResponse extends Pagination {
  orders: DemoOrderListItem[]
  highestPurchaseAmount: number
  orderStatuses: string[]
  orderTypes: string[]
}

export interface ItemHistory {
  formattedOrderDate: string
  itemId: string
  productName: string
  orderId: number
}

export interface ItemHistoryDemo extends ItemHistory {
  orderType: string
}

export interface ItemHistoryDemoCustomer extends ItemHistoryDemo {
  customerName: string
  confirmationEmail: string
  customerId: string
}

export interface ItemHistoryResponse extends Pagination {
  items: ItemHistory[]
}

export interface ItemHistoryDemoResponse extends Pagination {
  items: ItemHistoryDemo[]
  orderTypes: string[]
}

export interface ItemHistoryDemoCustomerResponse extends Pagination {
  items: ItemHistoryDemoCustomer[]
}

export interface ActionsNeeded {
  needsDemoDirectorySla: boolean
  needsDemoLocatorSla: boolean
  needsDirectDepositAccount: boolean
  needsIdaAcceptance: boolean
  needsGstIDConfirmation : boolean
  needsGstNumberConfirmation : boolean
  // Optional: not populated for anonymous sessions (see getAnonymousUser in composables/auth.ts)
  needsVatNumberFormatCorrection?: boolean
  needsTeamLeaderSelection: boolean
}

export interface DemoNotification {
 notificationId: number
 text: string
}

export interface AccountNotices {
  vatRegistration: string | undefined
  billing: string | undefined
  agreements: string | undefined
  settings: string | undefined
}
export interface User {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  isoBirthday: string
  birthday: string
  preferredContact: string
  preferredLanguage: Language
  defaultCulture: Culture
  culture: string
  address?: Address
  role: UserRole
  paymentInformation?: RecurlyPaymentInformation
  demonstrator?: Demonstrator
  errors: unknown[]
  receiveCommunications: boolean
  actionsNeeded: ActionsNeeded
  isBetaUser: boolean
  loyaltyOptIn: boolean
  notifications?: DemoNotification[]
  // Paper Pumpkin subscription state, read by the GTM user segmentation.
  isNewSubscriber?: boolean
  isLapsedSubscriber?: boolean
}

export enum SubscriptionType {
  PRIMARY = 'Primary',
  PREPAID = 'Prepaid',
}
export enum SubscriptionStatus {
  ACTIVE = 'Active',
  CANCELLED = 'Canceled',
  FAILED = 'Failed',
  SKIPPED = 'Paused',
}
export interface SubscriptionItem {
  id: string
  culture: string
  demoId: number
  demoTitle: number
  userId: string
  createdDate: string
  startsAt: string
  subscriptionType: string
  planCode: string
  status: SubscriptionStatus
  subscriptionId: string
  qtyOrdered: number
  initialItemId: string
  prePaidMonths: number
  subType: SubscriptionType
  giftEmail: string
  giftMessage: string
  gifteeRecurlyId: string
  prePaidRecurlyId: string
  lastModifiedDate: string
  skippedMonth: string
  nextKitShipMonth: string
  resumeShipMonth: string
  amount: number
  remainingMonths: number
  image: string
  recurlySubscriptionId: string
  subscriptionTitle: string
  formattedAddress: string
  isGift: boolean
  isClaimed: boolean
  formattedAmount: string
  legacyPrePaidCode?: string
  formattedNextBillDate: string
  formattedCancelDate?: string
  isSkipped: boolean
  canOrder: boolean
  billingError: ApiErrorResponse
}

export interface SubscriptionDetail extends SubscriptionItem, Omit<InvoiceSubscription, 'status'> {
  orderConfirmationNumber: string
  formattedLastBilledAmount: string
  canEditQuantity: boolean
  subscriptionTitle: string
  formattedLastBilledDate: string
  canSkip: boolean
  canCancel: boolean
  noSkipReasonText: string
  cancelFirstMonthText?: string
  loyaltyOptIn: boolean
  loyaltyRewardsEarned: number
  formattedLoyaltyRewardsEarned: string
  isFirstMonth: boolean
}
export interface SubscriptionManagementResponse {
  message: string
  subscriptions: SubscriptionItem[]
  canOrder: boolean
}

export interface SubscriptionFulfillmentItem {
  id: string
  culture: string
  userId: string
  subscriptionType: number
  subscriptionId: string
  initialItemId: string
  prePaidMonths: number
  subType: number
  amount: number
  subscriptionTitle: string
  formattedAmount: string
  formattedAddress: string
  isGift: boolean
  orderId: number
  demoId: number
  formattedCreatedDate: string
}
export interface SubscriptionFulfillmentResponse extends Pagination {
  subscriptionHistoryList: SubscriptionFulfillmentItem[]
}

// nuxt2 splits the group-shipping payload in two: the address half (GroupShippingBase),
// which the API echoes back on the code model, and the recipient/payment half
// (GroupShippingCreateUpdateBase), which only the write calls send. Keeping the split means
// GroupShippingUpdate stays the narrow PATCH body it actually is -- email plus payment --
// instead of the full address duplicate this file used to declare.
export interface GroupShippingBase {
  firstName: string
  lastName: string
  addressLine1: string
  addressLine2: string
  addressLine3: string
  city: string
  suburb: string
  county: string
  postalCode: string
  region: string
  country: string
  phoneNumber: string
  // EU only: the per-order shipping fee the demonstrator sets when creating the code.
  shippingChargePerOrder?: number
}

export interface GroupShippingCreateUpdateBase {
  email: string
  billingInfoId?: string
  paymentAccountId?: string
  isTempPayment?: boolean
  paymentIsOwnedByDemo?: boolean
}

export enum GroupShippingShipTo {
  Customer = 'Customer',
  Demonstrator = 'Demonstrator',
  Other = 'Other'
}

export interface GroupShippingCreate extends GroupShippingCreateUpdateBase, GroupShippingBase {
  customerId: string
  shipTo: GroupShippingShipTo
}

// The PATCH body is exactly the recipient/payment half, with no address fields -- an existing
// code's shipping address is fixed. Aliased rather than an empty extending interface so the
// operation still has its own name without tripping no-empty-object-type.
export type GroupShippingUpdate = GroupShippingCreateUpdateBase

export enum GroupShippingCodeStatus {
  Active = 'active',
  Expired = 'expired',
  Complete = 'complete',
  InUse = 'inUse',
  None = ''
}

export interface GroupShippingOrder {
  orderId: string
  orderDate: string
  firstName: string
  lastName: string
  // Decide which detail route the order number links to. An order placed by
  // another demonstrator against this code links nowhere -- see GroupShippingView.
  isCustomerOrder: boolean
  isDemosOwnOrder: boolean
}

export interface GroupShippingCodeModel extends GroupShippingBase {
  email: string
  id: string
  culture: string
  demoId: number
  status: GroupShippingCodeStatus
  groupShippingCode: string
  expirationDate: string
  shippingMethod: string
  orders: GroupShippingOrder[]
  payment: DemoPaymentInformation
  formattedShippingChargePerOrder?: string
}

export interface GroupShippingResponse {
  apiError: ApiErrorResponse
  groupShippingCodeModel: GroupShippingCodeModel
}

export interface GroupShippingDashboardModel {
  id: string
  culture: string
  demoId: number
  groupShippingCode: string
  expirationDate: string
  firstName: string
  lastName: string
  // NOT `orderSubmittedCount`. This file carried the singular spelling, which type-checks
  // fine and renders an empty Order Qty column, because nothing consumed it until now.
  ordersSubmittedCount: number
}

export interface GroupShippingDashboardResponse {
  apiError: ApiErrorResponse
  message: string
  groupShippingDashboardModels: GroupShippingDashboardModel[]
}

export interface GroupShippingListResponse {
  apiError: ApiErrorResponse
  message: string
  groupShippingCodeModels: GroupShippingCodeModel[]
}

export interface GroupShippingMinimumChargeResponse {
  statusCode: number
  message: string
  shippingCharge: number
  formattedShippingCharge: string
  shipToCulture: string
}

export interface GroupShippingMinimumChargeRequest {
  email: string
  country: string
  customerId: string
  shipTo: GroupShippingShipTo
}

export enum GroupShippingValidationStatus {
  Valid = 'Valid',
  NotValid = 'NotValid',
  Expired = 'Expired'
}

export interface GroupShippingValidation {
  demoId: number
  groupShippingCode: string
  status: GroupShippingValidationStatus
}

export enum GroupShippingCreateAllowedStatus {
  Allowed,
  MaxReached,
  // Dropped when this enum was first ported; the API still returns 2 for a
  // sanctioned account, and the customers table has to tell that apart from
  // MaxReached because the two show different messages.
  HasSanction
}

export interface GroupShippingFormatCurrencyRequest {
  value: number
  culture: string
}
